import { type Mutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import type { SerializedObject, UnpackResponse } from "../../../api/src/lib";
import type { ModelsController } from "../../../api/src/models/models.controller";
import type {
  Benchmark,
  ExtendedBenchmark,
  ExtendedModelSleeve,
  ExtendedSecurityGroup,
  Model,
  ModelPerformance,
  ModelRequestSecurities,
  PrimarySecurity,
  SecurityGroup,
  SecurityGroupRequest,
  SimpleAssignedItem,
  Sleeve,
} from "../../../api/src/models/models.service";
import type { RiskAllocation } from "../../../api/src/securities/securities.service";
import {
  deserializeDate,
  useAuthenticatedMutationNew,
  useAuthenticatedQuery,
} from "../lib/api";
import { useAuthenticatedFetchAllPages } from "../lib/pagination";

export function sumModelWeights(
  primarySecurities: PrimarySecurity[],
  securityGroups: SimpleAssignedItem[] = [],
  sleeves: SimpleAssignedItem[] = [],
) {
  return (
    sumWeights(primarySecurities) +
    sumWeights(sleeves) +
    sumWeights(securityGroups)
  );
}

function sumWeights(arr: { weight: number | string }[]) {
  return arr.reduce((sum, list) => {
    const weightStr = list.weight as unknown as string;
    return sum + parseFloat(weightStr === "" ? "0" : weightStr);
  }, 0);
}

export const primarySecurityHelpMessage =
  "Priority securities to have in a portfolio";
export const secondarySecurityHelpMessage =
  "Securities that can be used in place of primary securities in a portfolio";
export const securityGroupHelpMessage =
  "Groupings of a primary security and its associated secondary securities";
export const sleeveHelpMessage =
  "Sub-models that can be combined to form an assignable model";
export const securityRestrictionHelpMessage =
  "Securities that should remain held but that have no alternative security";

export const ModelsHelp = () => (
  <section>
    <p>
      Models are target portfolios that can be assigned to households and
      accounts. Models dictate what trades are proposed when generating
      household and account rebalances.
    </p>
    <h3>Models</h3>
    <p>
      <strong>Models</strong> are a combination of Primary Securities, Security
      Groups, and Sleeves. Each Primary Security, Security Group, and Sleeve is
      given a target weight within the portfolio.
    </p>
    <h3>Sleeves</h3>
    <p>
      <strong>Sleeves</strong> are sub-models that can be reused within multiple
      Models. They consist of Primary Securities and Security Groups, just like
      Models. <strong>Sleeves</strong> can not be directly assigned to a
      household or account. AdviceCloud does not allow the use of{" "}
      <strong>Sleeves</strong> within other <strong>Sleeves</strong>. The Asset
      Category and Region are optional labels that can help categorize{" "}
      <strong>Sleeves</strong>.
    </p>
    <h3>Security Groups</h3>
    <p>
      <strong>Security Groups</strong> consist of a primary security and its
      secondary securities. <strong>Security Groups</strong> can be assigned to
      models and sleeves just like Primary Securities. In a{" "}
      <strong>Security Group</strong>, Primary Securities are given priority
      over Secondary Securities when generating rebalances and proposing trades.
      Secondary Securities are used in place of Primary Securities in certain
      situations, such as for tax-loss harvesting or selling out of a position.
      Secondary Securities have other uses, such as facilitating a gradual
      migration from an old to a new Model without generating a large amount of
      trades immediately.
    </p>
    <h3>Benchmarks</h3>
    <p>
      <strong>Benchmarks</strong> are an optional feature of Models that allow
      comparison of a Model to another portfolio, such as one or more ETFs.
      Assigning a <strong>Benchmark</strong> to a Model is useful for indicating
      the intended theme of a Model. Benchmarks have no effect on trade
      proposals and are purely for informational purposes.
    </p>
  </section>
);

/**
 * The path of `getAllModels`. Also the FIRST element of every react-query key
 * the page walk in `useQueryModels` uses, so `[MODELS_PATH]` reaches every
 * page of both walks (`metrics=true` and `metrics=false`).
 */
export const MODELS_PATH = "/v1/models/models";

/** One item of `GET /models/models`, as the route serialises it. */
type SerializedModelListItem = SerializedObject<
  UnpackResponse<ModelsController["getAllModels"]>
>["data"][number];

/**
 * True for a create, update or delete in `collection`: the mutation keys
 * `useCreateModel`, `useUpdateModel` and `useDeleteModel` send for `"models"`,
 * `useCreateBenchmark`, `useUpdateBenchmark` and `useDeleteBenchmark` for
 * `"benchmarks"`, `useCreateSleeve`, `useUpdateSleeve` and
 * `useDeleteSleeve` for `"sleeves"`, and `useCreateSecurityGroup`,
 * `useUpdateSecurityGroup` and `useDeleteSecurityGroup` for
 * `"security-groups"`.
 */
const isWriteTo = (
  mutation: Mutation<unknown, unknown, unknown>,
  collection: "models" | "benchmarks" | "sleeves" | "security-groups",
) => {
  const [method, version, root, written] = mutation.options.mutationKey ?? [];
  return (
    ["POST", "PUT", "DELETE"].includes(String(method)) &&
    version === "v1" &&
    root === "models" &&
    written === collection
  );
};

/**
 * Drops every cached page of the model list, so the next walk reads it again
 * rather than being handed a page from before a write. Invalidating is not
 * enough: a finished walk holds no observer on its first page, so a restarted
 * walk would be handed the cached page and stop there (`useRebalanceBoards`,
 * `rebalances/boards.ts`, for the same reason).
 */
function removeModelListPages(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.removeQueries({ queryKey: [MODELS_PATH] });
}

/**
 * The path of `getSleeveAssignments` for `sleeveId`. Also the FIRST element of
 * every react-query key the page walk in `useQuerySleeveAssignments` uses.
 */
export const sleeveAssignmentsPath = (sleeveId: number) =>
  `/v1/models/sleeves/${sleeveId}/assignments`;

/**
 * The path of `getSecurityGroupAssignments` for `securityGroupId`. Also the
 * FIRST element of every react-query key the page walk in
 * `useQuerySecurityGroupAssignments` uses.
 */
export const securityGroupAssignmentsPath = (securityGroupId: number) =>
  `/v1/models/security-groups/${securityGroupId}/assignments`;

/**
 * Drops every cached page of every sleeve's assignment list
 * (`sleeveAssignmentsPath`, any id), for the reason `removeModelListPages`
 * gives. A model write changes which models use a sleeve.
 */
function removeSleeveAssignmentPages(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  queryClient.removeQueries({
    predicate: ({ queryKey: [path] }) =>
      typeof path === "string" &&
      /^\/v1\/models\/sleeves\/\d+\/assignments$/.test(path),
  });
}

/**
 * Drops every cached page of every security group's assignment list
 * (`securityGroupAssignmentsPath`, any id), for the reason
 * `removeModelListPages` gives. A model write or a sleeve write changes which
 * models and sleeves use a security group.
 */
function removeSecurityGroupAssignmentPages(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  queryClient.removeQueries({
    predicate: ({ queryKey: [path] }) =>
      typeof path === "string" &&
      /^\/v1\/models\/security-groups\/\d+\/assignments$/.test(path),
  });
}

/**
 * Every row of an assignment list (`getSleeveAssignments` or
 * `getSecurityGroupAssignments`), walked a page at a time
 * (`useAuthenticatedFetchAllPages`), with `data` held back until the last page
 * is in. When a write to one of `writtenBy` succeeds, the cached pages are
 * dropped (`removePages`) and the walk starts again, as `useQueryModels` does
 * for a model write.
 *
 * `path` names one id, and the walk collects rows per mounted hook, so a
 * caller whose id can change remounts the hook on the change (a React `key`),
 * as `lib/pagination.ts` requires, and never mounts it for id `0`.
 */
/** The writes that change which models use a sleeve. */
const SLEEVE_ASSIGNMENT_WRITES = ["models"] as const;

/** The writes that change which models and sleeves use a security group. */
const SECURITY_GROUP_ASSIGNMENT_WRITES = ["models", "sleeves"] as const;

function useAllAssignments<TRow>(
  path: string,
  writtenBy: readonly ("models" | "sleeves")[],
  removePages: (queryClient: ReturnType<typeof useQueryClient>) => void,
) {
  const queryClient = useQueryClient();
  const { rows, hasEveryRow, isError, reload } =
    useAuthenticatedFetchAllPages<TRow>(path);

  useEffect(
    () =>
      queryClient.getMutationCache().subscribe((event) => {
        if (
          event.type === "updated" &&
          event.action.type === "success" &&
          writtenBy.some((collection) => isWriteTo(event.mutation, collection))
        ) {
          removePages(queryClient);
          reload();
        }
      }),
    [queryClient, reload, writtenBy, removePages],
  );

  const data: TRow[] | undefined = useMemo(
    () => (hasEveryRow ? rows : undefined),
    [rows, hasEveryRow],
  );

  const isPending = !hasEveryRow && !isError;

  return { isPending, isLoading: isPending, isError, data };
}

/**
 * Every model of the firm. `GET /models/models` answers a page (APP-1650), and
 * every screen that reads this hook shows or searches the whole list (the model
 * selectors of the account and household screens, `useAccountResolver`,
 * `Models.tsx`, `ModelInfo.tsx`), so this walks every page
 * (`useAuthenticatedFetchAllPages`) and holds `data` back until the last one
 * is in. Each record goes through `deserializeModelMetricPercents`, as before.
 *
 * When a model write settles, the walk starts again, so a mounted list shows
 * the write.
 */
export function useQueryModels({
  includeMetrics = false,
  enabled = true,
}: { includeMetrics?: boolean; enabled?: boolean } = {}) {
  const queryClient = useQueryClient();
  const { rows, hasEveryRow, isError, reload } =
    useAuthenticatedFetchAllPages<SerializedModelListItem>(
      MODELS_PATH,
      {
        metrics: String(includeMetrics),
      },
      { enabled },
    );

  // Restart the walk when a model write succeeds. Subscribed to the mutation
  // cache's events rather than read from `useIsMutating`: a write that starts
  // and settles within one render batch never shows a pending count.
  useEffect(
    () =>
      queryClient.getMutationCache().subscribe((event) => {
        if (
          event.type === "updated" &&
          event.action.type === "success" &&
          isWriteTo(event.mutation, "models")
        ) {
          removeModelListPages(queryClient);
          reload();
        }
      }),
    [queryClient, reload],
  );

  const data: Model[] | undefined = useMemo(
    () =>
      hasEveryRow
        ? rows.map((model) =>
            deserializeModel(deserializeModelMetricPercents(model)),
          )
        : undefined,
    [rows, hasEveryRow],
  );

  const isPending = !hasEveryRow && !isError;

  return { isPending, isLoading: isPending, isError, data };
}

export function useQueryModel(modelId: number, { enabled = true } = {}) {
  return useAuthenticatedQuery<
    UnpackResponse<ModelsController["getModel"]>,
    Model
  >({
    queryKey: ["v1", "models", "models", modelId],
    queryFn: async (model) => deserializeModel(model.data),
    queryOptions: { enabled },
  });
}

export function useCreateModel() {
  const queryClient = useQueryClient();

  return useAuthenticatedMutationNew<
    UnpackResponse<ModelsController["createModel"]>,
    Model
  >({
    mutationKey: ["POST", "v1", "models", "models"],
    mutationFn: ({ data }) => {
      removeModelListPages(queryClient);
      removeSleeveAssignmentPages(queryClient);
      removeSecurityGroupAssignmentPages(queryClient);

      return deserializeModel(data);
    },
  });
}

export function useUpdateModel(modelId: number) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutationNew<
    UnpackResponse<ModelsController["updateModel"]>,
    Model
  >({
    mutationKey: ["PUT", "v1", "models", "models", modelId],
    mutationFn: ({ data }) => {
      queryClient.invalidateQueries({
        queryKey: ["v1", "models", "models", modelId, { includeMetrics: true }],
      });
      // APP-2070 (R127): read the summary again, so "Flattened Model" shows
      // the saved model.
      queryClient.invalidateQueries({
        queryKey: ["v1", "models", "models", modelId, "summary"],
      });
      removeModelListPages(queryClient);
      removeSleeveAssignmentPages(queryClient);
      removeSecurityGroupAssignmentPages(queryClient);

      return deserializeModel(data);
    },
  });
}

export function useQueryModelAssignments(modelId: number) {
  return useAuthenticatedQuery<
    UnpackResponse<ModelsController["getModelAssignments"]>,
    {
      assignedAccountCount: number;
      assignedHouseholdCount: number;
    }
  >({
    queryKey: ["v1", "models", "models", modelId, "assignments"],
    queryFn: async (assignments) => assignments.data,
    queryOptions: { enabled: modelId !== 0 },
  });
}

/**
 * Deletes a model, first moving the firm's accounts and households on it to
 * `newModelId` (`DELETE /models/models/:id?newModelId=`, APP-1650). The API
 * does both in one transaction. `ModelDeleteDialog` passes `0` when no
 * replacement model is selected, which the API reads as "clear their model".
 */
export function useDeleteModel(oldModelId: number, newModelId: number) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutationNew<
    UnpackResponse<ModelsController["deleteModel"]>,
    UnpackResponse<ModelsController["deleteModel"]>["data"],
    void
  >({
    mutationKey: ["DELETE", "v1", "models", "models", oldModelId, newModelId],
    mutationFn: ({ data }) => {
      queryClient.invalidateQueries({
        queryKey: ["v1", "models", "models", oldModelId],
      });
      removeModelListPages(queryClient);
      removeSleeveAssignmentPages(queryClient);
      removeSecurityGroupAssignmentPages(queryClient);

      return data;
    },
    // The old `urlBuilderFn` compared the numeric `newModelId` with the
    // string "0", so it always sent `?newModelId=<n>`, `0` included. Sending
    // it always keeps that: with no replacement selected, the firm's accounts
    // and households on the model have their model cleared.
    urlBuilderFn: ([version, root, collection, id, replacementId]) =>
      `/${version}/${root}/${collection}/${id}?newModelId=${replacementId}`,
  });
}

export function deserializeModel<TModel extends Model>(
  model: SerializedObject<TModel>,
) {
  return {
    ...model,
    createdTime: deserializeDate(model.createdTime),
    updatedTime: deserializeDate(model.updatedTime),
  } as TModel;
}

/**
 * The decimal places of a model summary weight on 0 to 1: the API sends 6
 * places on 0 to 100 (`api/src/models/models.controller.ts`,
 * `MODEL_SUMMARY_WEIGHT_PERCENT_DECIMALS`), so 8 on 0 to 1.
 */
const MODEL_SUMMARY_WEIGHT_RATIO_DECIMALS = 8;

/**
 * `GET /models/models/:id/summary` sends each `primarySecurities.<symbol>.weight`
 * on 0 to 100 (`an-decision-01a0ef4b7e9377c9` row 1). Dividing by 100 here keeps
 * the console's readers (`ModelInfo.tsx`, `ModelSummary.tsx`) on the 0 to 1
 * ratio they display. The quotient is rounded to
 * {@link MODEL_SUMMARY_WEIGHT_RATIO_DECIMALS}, so `55` comes back as `0.55` and
 * not as a neighbouring double.
 */
export function deserializeModelSummary<
  TSummary extends { primarySecurities: Record<string, PrimarySecurity> },
>(summary: TSummary): TSummary {
  return {
    ...summary,
    primarySecurities: Object.fromEntries(
      Object.entries(summary.primarySecurities).map(([symbol, security]) => [
        symbol,
        {
          ...security,
          weight:
            Math.round(
              security.weight * 10 ** (MODEL_SUMMARY_WEIGHT_RATIO_DECIMALS - 2),
            ) /
            10 ** MODEL_SUMMARY_WEIGHT_RATIO_DECIMALS,
        },
      ]),
    ),
  };
}

/**
 * The decimal places of a calculator or list percent on 0 to 1: the API
 * sends 6 places on 0 to 100 (`api/src/models/models.controller.ts`,
 * `METRIC_PERCENT_DECIMALS`), so 8 on 0 to 1.
 */
const METRIC_RATIO_DECIMALS = 8;

/**
 * `percent` (0 to 100) on 0 to 1, rounded to {@link METRIC_RATIO_DECIMALS}, so
 * `7` comes back as `0.07` and `0.7777` as `0.007777`, not as a neighbouring
 * double. `null` (a metric FastTrack has no number for, such as a five-year
 * return of a model holding a younger fund) stays `null`, so the readers show
 * "N/A" rather than "0.00%".
 */
function metricRatio(percent: number): number;
function metricRatio(percent: number | null): number | null;
function metricRatio(percent: number | null): number | null {
  if (percent === null || percent === undefined) return null;
  return (
    Math.round(percent * 10 ** (METRIC_RATIO_DECIMALS - 2)) /
    10 ** METRIC_RATIO_DECIMALS
  );
}

/** Each risk level's share of `allocation` on 0 to 1 in place of 0 to 100. */
export function deserializeRiskAllocationPercents(
  allocation: RiskAllocation,
): RiskAllocation {
  return Object.fromEntries(
    Object.entries(allocation).map(([riskLevel, percent]) => [
      riskLevel,
      metricRatio(percent),
    ]),
  );
}

function returnsRatios(
  returns: ModelPerformance["returns"],
): ModelPerformance["returns"] {
  return {
    ...returns,
    oneYear: metricRatio(returns.oneYear),
    threeYear: metricRatio(returns.threeYear),
    fiveYear: metricRatio(returns.fiveYear),
  };
}

function riskRatios(risk: ModelPerformance["risk"]): ModelPerformance["risk"] {
  return { ...risk, alpha: metricRatio(risk.alpha) };
}

/**
 * `POST /models/performance` sends `returns.*`, `dividendYield`, `risk.alpha`
 * and `expenseRatio` on 0 to 100 (`an-decision-01a0ef4b7e9377c9` row 1).
 * Dividing by 100 here keeps `ModelPerformance.tsx` on the 0 to 1 ratio
 * `formatPercent` reads. FastTrack sends a fund's expense ratio as a
 * fraction (`"0.000300"` for 0.03%), which the API multiplies by 100
 * (`docs/adr/models-domain-admission.md`), so `0.03` shows as "0.03%".
 */
export function deserializeModelPerformance(
  performance: ModelPerformance,
): ModelPerformance {
  return {
    ...performance,
    returns: returnsRatios(performance.returns),
    expenseRatio: metricRatio(performance.expenseRatio),
    dividendYield: metricRatio(performance.dividendYield),
    risk: riskRatios(performance.risk),
  };
}

/**
 * `GET /models/models`, `/models/benchmarks` and `/models/sleeves` with
 * `metrics=true` send `returns.*`, `dividendYield` and `risk.alpha` on 0 to
 * 100. Dividing by 100 here keeps the lists' readers (`Models.tsx`,
 * `Benchmarks.tsx` and `Sleeves.tsx`, through `formatPercent`) on 0 to 1. A
 * `null` metric (every one with `metrics=false`) stays `null`. `expenseRatio`
 * is on 0 to 100 as `deserializeModelPerformance` states, and is divided
 * too.
 */
export function deserializeListPerformancePercents<
  TRecord extends {
    returns: ModelPerformance["returns"] | null;
    expenseRatio: number | null;
    dividendYield: number | null;
    risk: ModelPerformance["risk"] | null;
  },
>(record: TRecord): TRecord {
  return {
    ...record,
    returns: record.returns === null ? null : returnsRatios(record.returns),
    expenseRatio:
      record.expenseRatio === null ? null : metricRatio(record.expenseRatio),
    dividendYield:
      record.dividendYield === null ? null : metricRatio(record.dividendYield),
    risk: record.risk === null ? null : riskRatios(record.risk),
  };
}

/**
 * `GET /models/models` and `/models/benchmarks` with `metrics=true` also send
 * each `riskAllocations.<risk level>` on 0 to 100. Dividing by 100 here keeps
 * `getRiskAllocationDisplay` (`Models.tsx`, `Benchmarks.tsx`) on 0 to 1. A
 * `null` `riskAllocations` stays `null`.
 */
export function deserializeModelMetricPercents<
  TModel extends {
    returns: ModelPerformance["returns"] | null;
    expenseRatio: number | null;
    dividendYield: number | null;
    risk: ModelPerformance["risk"] | null;
    riskAllocations: RiskAllocation | null;
  },
>(model: TModel): TModel {
  return {
    ...deserializeListPerformancePercents(model),
    riskAllocations:
      model.riskAllocations === null
        ? null
        : deserializeRiskAllocationPercents(model.riskAllocations),
  };
}

/**
 * The path of `getAllSleeves`. Also the FIRST element of every react-query key
 * the page walk in `useQuerySleeves` uses, so `[SLEEVES_PATH]` reaches every
 * page of both walks (`metrics=true` and `metrics=false`).
 */
export const SLEEVES_PATH = "/v1/models/sleeves";

/** One item of `GET /models/sleeves`, as the route serialises it. */
type SerializedSleeveListItem = SerializedObject<
  UnpackResponse<ModelsController["getAllSleeves"]>
>["data"][number];

/**
 * Drops every cached page of the sleeve list, for the reason
 * `removeModelListPages` gives.
 */
function removeSleeveListPages(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.removeQueries({ queryKey: [SLEEVES_PATH] });
}

/**
 * Every sleeve of the firm. `GET /models/sleeves` answers a page (APP-1650),
 * and both readers of this hook show the whole list (`Sleeves.tsx`, and
 * `SleevesSelector.tsx` with `metrics=false`), so this walks every page
 * (`useAuthenticatedFetchAllPages`) and holds `data` back until the last one
 * is in. Each record goes through `deserializeListPerformancePercents`, as
 * before.
 *
 * When a sleeve write settles, the walk starts again, so a mounted list shows
 * the write.
 */
export function useQuerySleeves({ includeMetrics = false } = {}) {
  const queryClient = useQueryClient();
  const { rows, hasEveryRow, isError, reload } =
    useAuthenticatedFetchAllPages<SerializedSleeveListItem>(SLEEVES_PATH, {
      metrics: String(includeMetrics),
    });

  // Restart the walk when a sleeve write succeeds, as `useQueryModels` does
  // for a model write.
  useEffect(
    () =>
      queryClient.getMutationCache().subscribe((event) => {
        if (
          event.type === "updated" &&
          event.action.type === "success" &&
          isWriteTo(event.mutation, "sleeves")
        ) {
          removeSleeveListPages(queryClient);
          reload();
        }
      }),
    [queryClient, reload],
  );

  const data: ExtendedModelSleeve[] | undefined = useMemo(
    () =>
      hasEveryRow
        ? rows.map((sleeve) =>
            deserializeSleeve(deserializeListPerformancePercents(sleeve)),
          )
        : undefined,
    [rows, hasEveryRow],
  );

  const isPending = !hasEveryRow && !isError;

  return { isPending, isLoading: isPending, isError, data };
}

export function useQuerySleeve(sleeveId: number, { enabled = true } = {}) {
  return useAuthenticatedQuery<
    UnpackResponse<ModelsController["getSleeve"]>,
    Sleeve
  >({
    queryKey: ["v1", "models", "sleeves", sleeveId],
    queryFn: async (sleeve) => deserializeSleeve(sleeve.data),
    queryOptions: { enabled },
  });
}

export function useCreateSleeve() {
  const queryClient = useQueryClient();

  return useAuthenticatedMutationNew<
    UnpackResponse<ModelsController["createSleeve"]>,
    Sleeve
  >({
    mutationKey: ["POST", "v1", "models", "sleeves"],
    mutationFn: ({ data }) => {
      removeSleeveListPages(queryClient);
      removeSecurityGroupAssignmentPages(queryClient);

      return deserializeSleeve(data);
    },
  });
}

export function useUpdateSleeve(sleeveId: number) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutationNew<
    UnpackResponse<ModelsController["updateSleeve"]>,
    Sleeve
  >({
    mutationKey: ["PUT", "v1", "models", "sleeves", sleeveId],
    mutationFn: ({ data }) => {
      queryClient.invalidateQueries({
        queryKey: [
          "v1",
          "models",
          "sleeves",
          sleeveId,
          { includeMetrics: true },
        ],
      });
      removeSleeveListPages(queryClient);
      removeSecurityGroupAssignmentPages(queryClient);

      return deserializeSleeve(data);
    },
  });
}

/**
 * Every model that uses the sleeve. `GET /models/sleeves/:id/assignments`
 * answers a page (APP-1650), and `SleeveDeleteDialog.tsx` must see every
 * model before it offers the delete, so this walks every page
 * (`useAllAssignments`). A model write restarts the walk. Mount it for a real
 * sleeve id only, keyed by the id.
 */
export function useQuerySleeveAssignments(sleeveId: number) {
  return useAllAssignments<
    UnpackResponse<ModelsController["getSleeveAssignments"]>["data"][number]
  >(
    sleeveAssignmentsPath(sleeveId),
    SLEEVE_ASSIGNMENT_WRITES,
    removeSleeveAssignmentPages,
  );
}

export function useDeleteSleeve(sleeveId: number) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutationNew<
    UnpackResponse<ModelsController["deleteSleeve"]>,
    void,
    void
  >({
    mutationKey: ["DELETE", "v1", "models", "sleeves", sleeveId],
    mutationFn: () => {
      queryClient.invalidateQueries({
        queryKey: ["v1", "models", "sleeves", sleeveId],
      });
      removeSleeveListPages(queryClient);
      removeSecurityGroupAssignmentPages(queryClient);
    },
  });
}

export function deserializeSleeve<TSleeve extends Sleeve>(
  sleeve: SerializedObject<TSleeve>,
) {
  return {
    ...sleeve,
    createdTime: deserializeDate(sleeve.createdTime),
    updatedTime: deserializeDate(sleeve.updatedTime),
  } as TSleeve;
}

/**
 * The path of `getAllBenchmarks`. Also the FIRST element of every react-query
 * key the page walk in `useQueryBenchmarks` uses, so `[BENCHMARKS_PATH]`
 * reaches every page of both walks (`metrics=true` and `metrics=false`).
 */
export const BENCHMARKS_PATH = "/v1/models/benchmarks";

/** One item of `GET /models/benchmarks`, as the route serialises it. */
type SerializedBenchmarkListItem = SerializedObject<
  UnpackResponse<ModelsController["getAllBenchmarks"]>
>["data"][number];

/**
 * Drops every cached page of the benchmark list, for the reason
 * `removeModelListPages` gives.
 */
function removeBenchmarkListPages(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  queryClient.removeQueries({ queryKey: [BENCHMARKS_PATH] });
}

/**
 * Every benchmark of the firm. `GET /models/benchmarks` answers a page
 * (APP-1650), and both readers of this hook show the whole list
 * (`Benchmarks.tsx`, and the benchmark selector of `ModelInfo.tsx`), so this
 * walks every page (`useAuthenticatedFetchAllPages`) and holds `data` back
 * until the last one is in. Each record goes through
 * `deserializeModelMetricPercents`, as before.
 *
 * When a benchmark write settles, the walk starts again, so a mounted list
 * shows the write.
 */
export function useQueryBenchmarks({ includeMetrics = false } = {}) {
  const queryClient = useQueryClient();
  const { rows, hasEveryRow, isError, reload } =
    useAuthenticatedFetchAllPages<SerializedBenchmarkListItem>(
      BENCHMARKS_PATH,
      { metrics: String(includeMetrics) },
    );

  // Restart the walk when a benchmark write succeeds, as `useQueryModels`
  // does for a model write.
  useEffect(
    () =>
      queryClient.getMutationCache().subscribe((event) => {
        if (
          event.type === "updated" &&
          event.action.type === "success" &&
          isWriteTo(event.mutation, "benchmarks")
        ) {
          removeBenchmarkListPages(queryClient);
          reload();
        }
      }),
    [queryClient, reload],
  );

  const data: ExtendedBenchmark[] | undefined = useMemo(
    () =>
      hasEveryRow
        ? rows.map((benchmark) =>
            deserializeBenchmark(deserializeModelMetricPercents(benchmark)),
          )
        : undefined,
    [rows, hasEveryRow],
  );

  const isPending = !hasEveryRow && !isError;

  return { isPending, isLoading: isPending, isError, data };
}

export function useQueryBenchmark(
  benchmarkId: number,
  { enabled = true } = {},
) {
  return useAuthenticatedQuery<
    UnpackResponse<ModelsController["getBenchmark"]>,
    Benchmark
  >({
    queryKey: ["v1", "models", "benchmarks", benchmarkId],
    queryFn: async (benchmark) => deserializeBenchmark(benchmark.data),
    queryOptions: { enabled },
  });
}

export function useCreateBenchmark() {
  const queryClient = useQueryClient();

  return useAuthenticatedMutationNew<
    UnpackResponse<ModelsController["createBenchmark"]>,
    Benchmark
  >({
    mutationKey: ["POST", "v1", "models", "benchmarks"],
    mutationFn: ({ data }) => {
      removeBenchmarkListPages(queryClient);

      return deserializeBenchmark(data);
    },
  });
}

export function useUpdateBenchmark(benchmarkId: number) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutationNew<
    UnpackResponse<ModelsController["updateBenchmark"]>,
    Benchmark
  >({
    mutationKey: ["PUT", "v1", "models", "benchmarks", benchmarkId],
    mutationFn: ({ data }) => {
      queryClient.invalidateQueries({
        queryKey: [
          "v1",
          "models",
          "benchmarks",
          benchmarkId,
          { includeMetrics: true },
        ],
      });
      removeBenchmarkListPages(queryClient);

      return deserializeBenchmark(data);
    },
  });
}

export function useDeleteBenchmark(benchmarkId: number) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutationNew<
    UnpackResponse<ModelsController["deleteBenchmark"]>,
    void,
    void
  >({
    mutationKey: ["DELETE", "v1", "models", "benchmarks", benchmarkId],
    mutationFn: () => {
      queryClient.invalidateQueries({
        queryKey: ["v1", "models", "benchmarks", benchmarkId],
      });
      removeBenchmarkListPages(queryClient);
    },
  });
}

export function deserializeBenchmark<TBenchmark extends Benchmark>(
  benchmark: SerializedObject<TBenchmark>,
) {
  return {
    ...benchmark,
    createdTime: deserializeDate(benchmark.createdTime),
    updatedTime: deserializeDate(benchmark.updatedTime),
  } as TBenchmark;
}

/**
 * The path of `getAllSecurityGroups`. Also the FIRST element of every
 * react-query key the page walk in `useQuerySecurityGroups` uses, so
 * `[SECURITY_GROUPS_PATH]` reaches every page of both walks (`metrics=true`
 * and `metrics=false`).
 */
export const SECURITY_GROUPS_PATH = "/v1/models/security-groups";

/** One item of `GET /models/security-groups`, as the route serialises it. */
type SerializedSecurityGroupListItem = SerializedObject<
  UnpackResponse<ModelsController["getAllSecurityGroups"]>
>["data"][number];

/**
 * Drops every cached page of the security-group list, for the reason
 * `removeModelListPages` gives.
 */
function removeSecurityGroupListPages(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  queryClient.removeQueries({ queryKey: [SECURITY_GROUPS_PATH] });
}

/**
 * Every security group of the firm. `GET /models/security-groups` answers a
 * page (APP-1650), and both readers of this hook show the whole list
 * (`SecurityGroups.tsx`, and `SecurityGroupsSelector.tsx` with
 * `metrics=false`, which the sleeve forms use to pick security groups), so
 * this walks every page (`useAuthenticatedFetchAllPages`) and holds `data`
 * back until the last one is in. Each record goes through
 * `deserializeSecurityGroup`, as before.
 *
 * When a security-group write settles, the walk starts again, so a mounted
 * list shows the write.
 */
export function useQuerySecurityGroups({ includeMetrics = false } = {}) {
  const queryClient = useQueryClient();
  const { rows, hasEveryRow, isError, reload } =
    useAuthenticatedFetchAllPages<SerializedSecurityGroupListItem>(
      SECURITY_GROUPS_PATH,
      { metrics: String(includeMetrics) },
    );

  // Restart the walk when a security-group write succeeds, as
  // `useQueryModels` does for a model write.
  useEffect(
    () =>
      queryClient.getMutationCache().subscribe((event) => {
        if (
          event.type === "updated" &&
          event.action.type === "success" &&
          isWriteTo(event.mutation, "security-groups")
        ) {
          removeSecurityGroupListPages(queryClient);
          reload();
        }
      }),
    [queryClient, reload],
  );

  const data: ExtendedSecurityGroup[] | undefined = useMemo(
    () =>
      hasEveryRow
        ? rows.map((securityGroup) =>
            deserializeSecurityGroup<ExtendedSecurityGroup>(securityGroup),
          )
        : undefined,
    [rows, hasEveryRow],
  );

  const isPending = !hasEveryRow && !isError;

  return { isPending, isLoading: isPending, isError, data };
}

export function useQuerySecurityGroup(
  securityGroupId: number,
  { enabled = true } = {},
) {
  return useAuthenticatedQuery<
    UnpackResponse<ModelsController["getSecurityGroup"]>,
    SecurityGroup
  >({
    queryKey: ["v1", "models", "security-groups", securityGroupId],
    queryFn: async (securityGroup) =>
      deserializeSecurityGroup(securityGroup.data),
    queryOptions: { enabled },
  });
}

export function useCreateSecurityGroup() {
  const queryClient = useQueryClient();

  return useAuthenticatedMutationNew<
    UnpackResponse<ModelsController["createSecurityGroup"]>,
    SecurityGroup,
    SecurityGroupRequest
  >({
    mutationKey: ["POST", "v1", "models", "security-groups"],
    mutationFn: ({ data }) => {
      removeSecurityGroupListPages(queryClient);

      return deserializeSecurityGroup(data);
    },
  });
}

export function useUpdateSecurityGroup(securityGroupId: number) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutationNew<
    UnpackResponse<ModelsController["updateSecurityGroup"]>,
    SecurityGroup,
    SecurityGroupRequest
  >({
    mutationKey: ["PUT", "v1", "models", "security-groups", securityGroupId],
    mutationFn: ({ data }) => {
      queryClient.invalidateQueries({
        queryKey: [
          "v1",
          "models",
          "security-groups",
          securityGroupId,
          { includeMetrics: true },
        ],
      });
      removeSecurityGroupListPages(queryClient);

      return deserializeSecurityGroup(data);
    },
  });
}

/**
 * Every model and sleeve that uses the security group.
 * `GET /models/security-groups/:id/assignments` answers a page (APP-1650), and
 * `SecurityGroupDeleteDialog.tsx` must see every one before it offers the
 * delete, so this walks every page (`useAllAssignments`). A model or sleeve
 * write restarts the walk. Mount it for a real security group id only, keyed
 * by the id.
 */
export function useQuerySecurityGroupAssignments(securityGroupId: number) {
  return useAllAssignments<
    UnpackResponse<
      ModelsController["getSecurityGroupAssignments"]
    >["data"][number]
  >(
    securityGroupAssignmentsPath(securityGroupId),
    SECURITY_GROUP_ASSIGNMENT_WRITES,
    removeSecurityGroupAssignmentPages,
  );
}

export function useDeleteSecurityGroup(securityGroupId: number) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutationNew<
    UnpackResponse<ModelsController["deleteSecurityGroup"]>,
    void,
    void
  >({
    mutationKey: ["DELETE", "v1", "models", "security-groups", securityGroupId],
    mutationFn: () => {
      queryClient.invalidateQueries({
        queryKey: ["v1", "models", "security-groups", securityGroupId],
      });
      removeSecurityGroupListPages(queryClient);
    },
  });
}

export function deserializeSecurityGroup<TSecurityGroup extends SecurityGroup>(
  securityGroup: SerializedObject<TSecurityGroup>,
) {
  return {
    ...securityGroup,
    createdTime: deserializeDate(securityGroup.createdTime),
    updatedTime: deserializeDate(securityGroup.updatedTime),
  } as TSecurityGroup;
}

function getModelSecuritiesRequest(model: Partial<ModelRequestSecurities>) {
  return {
    primarySecurities: (model?.primarySecurities ?? []).map((security) => ({
      ...security,
      weight:
        typeof security.weight === "number"
          ? security.weight
          : parseInt(security.weight),
    })),
    securityGroups: (model?.securityGroups ?? [])
      .filter(
        (securityGroup) =>
          securityGroup.id &&
          securityGroup.id !== -1 &&
          securityGroup.weight !== 0,
      )
      .map((securityGroup) => ({
        ...securityGroup,
        weight:
          typeof securityGroup.weight === "number"
            ? securityGroup.weight
            : parseInt(securityGroup.weight),
      })),
    sleeves: (model?.sleeves ?? [])
      .filter((sleeve) => sleeve.id && sleeve.id !== -1 && sleeve.weight !== 0)
      .map((sleeve) => ({
        ...sleeve,
        weight:
          typeof sleeve.weight === "number"
            ? sleeve.weight
            : parseInt(sleeve.weight),
      })),
  };
}

/** The key of the performance read: one per page, whatever the body. */
export const MODEL_PERFORMANCE_KEY = ["v1", "models", "performance"];

export function useQueryModelPerformance(
  model: Partial<ModelRequestSecurities>,
  { enabled = true } = {},
) {
  return useAuthenticatedQuery<
    UnpackResponse<ModelsController["getModelPerformance"]>,
    ModelPerformance
  >({
    queryKey: MODEL_PERFORMANCE_KEY,
    queryFn: async (modelPerformance) =>
      deserializeModelPerformance(modelPerformance.data),
    queryOptions: { enabled, gcTime: 0 },
    fetchOptions: {
      method: "POST",
      body: getModelSecuritiesRequest(model),
    },
  });
}

export function useQueryModelAssetAllocation(
  model: Partial<ModelRequestSecurities>,
  { enabled = true } = {},
) {
  return useAuthenticatedQuery<
    UnpackResponse<ModelsController["getModelAssetAllocation"]>,
    {
      category: string;
      allocation: number;
    }[]
  >({
    queryKey: ["v1", "models", "asset-allocation"],
    // The route sends each `allocation` on 0 to 100; the chart
    // (`AssetAllocationBarChart.tsx`) draws 0 to 1.
    queryFn: async (assetAllocation) =>
      assetAllocation.data.map((assetClass) => ({
        ...assetClass,
        allocation: metricRatio(assetClass.allocation),
      })),
    queryOptions: { enabled, gcTime: 0 },
    fetchOptions: {
      method: "POST",
      body: getModelSecuritiesRequest(model),
    },
  });
}

export function useQueryModelRiskAllocation(
  model: Partial<ModelRequestSecurities>,
  { enabled = true } = {},
) {
  return useAuthenticatedQuery<
    UnpackResponse<ModelsController["getModelRiskAllocation"]>,
    {
      current: RiskAllocation;
      target: RiskAllocation;
    }
  >({
    queryKey: ["v1", "models", "risk-allocation"],
    // The route sends each share on 0 to 100; `getRiskAllocation` reads 0 to 1.
    queryFn: async (riskAllocation) => ({
      current: deserializeRiskAllocationPercents(riskAllocation.data.current),
      target: deserializeRiskAllocationPercents(riskAllocation.data.target),
    }),
    queryOptions: { enabled, gcTime: 0 },
    fetchOptions: {
      method: "POST",
      body: getModelSecuritiesRequest(model),
    },
  });
}
